import type { AppConfig } from "../../types/appConfig";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";
import type {
  ExcursionsStats,
  PeriodStats,
  TrainingStats,
  StatsHandler,
  StatDate,
  InstructorStat,
  StatsDateRange,
  StatsGranularity,
} from "./Stats.typed";

const toNumber = (v: unknown, d = 0) => {
  const x = typeof v === "number" ? v : Number(v);
  return Number.isFinite(x) ? x : d;
};

const toText = (v: unknown) => (typeof v === "string" ? v : "");

const toNullableId = (v: unknown) => (v == null ? null : toNumber(v));

const toRecords = (v: unknown) =>
  Array.isArray(v) ? (v as unknown[]).map((item) => (item ?? {}) as Record<string, unknown>) : [];

export class StatsModel {
  zekayAPI: string;
  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }
  async getExcursionsStatsByYear(date: StatsHandler): Promise<ExcursionsStats[] | undefined> {
    const r = await fetchZekay(this.zekayAPI + "/excursions/stats/year", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(date),
    });
    await expectStatuses(r, 200, 204);
    if (r.status === 204) return undefined;
    return (await r.json()) as ExcursionsStats[];
  }
  async getExcursionsStatsByMonth(date: StatsHandler): Promise<ExcursionsStats> {
    const r = await fetchZekay(this.zekayAPI + "/excursions/stats/date", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(date),
    });
    await assertResponseOk(r);
    return (await r.json()) as ExcursionsStats;
  }
  async getPeriodStats(
    granularity: StatsGranularity,
    range: StatsDateRange,
  ): Promise<PeriodStats[]> {
    const params = new URLSearchParams({
      granularity,
      start_date: range.start,
      end_date: range.end,
    });
    const r = await fetchZekay(`${this.zekayAPI}/stats/periods?${params.toString()}`);
    await expectStatuses(r, 200, 204);
    if (r.status === 204) return [];
    const raw = (await r.json()) as unknown[];
    if (!Array.isArray(raw)) return [];
    return raw.map((row) => {
      const o = row as Record<string, unknown>;
      return {
        period_start: toText(o.period_start),
        done_count: toNumber(o.done_count),
        cancelled_count: toNumber(o.cancelled_count),
        groups_count: toNumber(o.groups_count),
        unique_instructors: toNumber(o.unique_instructors),
        instructor_slots: toNumber(o.instructor_slots),
        instructor_cancel_pct: toNumber(o.instructor_cancel_pct),
        instructor_unlisted_pct: toNumber(o.instructor_unlisted_pct),
        unique_clients: toNumber(o.unique_clients),
        client_slots: toNumber(o.client_slots),
        client_cancel_pct: toNumber(o.client_cancel_pct),
        client_unlisted_pct: toNumber(o.client_unlisted_pct),
        trainings_started: toNumber(o.trainings_started),
        trainings_validated: toNumber(o.trainings_validated),
        trainings_by_template: toRecords(o?.trainings_by_template).map((t) => ({
          training_template_id: toNullableId(t?.training_template_id),
          label: toText(t?.label),
          trainings_started: toNumber(t?.trainings_started),
          trainings_validated: toNumber(t?.trainings_validated),
        })),
        excursions_by_template: toRecords(o?.excursions_by_template).map((t) => ({
          excursion_template_id: toNullableId(t?.excursion_template_id),
          label: toText(t?.label),
          done_count: toNumber(t?.done_count),
          cancelled_count: toNumber(t?.cancelled_count),
          groups_count: toNumber(t?.groups_count),
        })),
        groups_by_template: toRecords(o?.groups_by_template).map((t) => ({
          group_template_id: toNullableId(t?.group_template_id),
          label: toText(t?.label),
          groups_count: toNumber(t?.groups_count),
          instructor_slots: toNumber(t?.instructor_slots),
          client_slots: toNumber(t?.client_slots),
        })),
      };
    });
  }
  async getStatDates(): Promise<StatDate[]> {
    const r = await fetchZekay(this.zekayAPI + "/dates");
    await assertResponseOk(r);
    return (await r.json()) as StatDate[];
  }
  async getTrainingStatsByYear(date: StatsHandler): Promise<TrainingStats[]> {
    const r = await fetchZekay(this.zekayAPI + "/trainings/stats/year", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(date),
    });
    await assertResponseOk(r);
    return (await r.json()) as TrainingStats[];
  }

  async getInstructorStats(
    range: StatsDateRange,
    instructorId?: number,
  ): Promise<InstructorStat[]> {
    const params = new URLSearchParams({ start_date: range.start, end_date: range.end });
    if (instructorId != null && instructorId >= 1) {
      params.set("instructor_id", String(instructorId));
    }
    const r = await fetchZekay(`${this.zekayAPI}/stats/instructors?${params.toString()}`);
    await expectStatuses(r, 200, 204);
    if (r.status === 204) return [];
    const raw = (await r.json()) as unknown[];
    if (!Array.isArray(raw)) return [];
    return raw.map((row) => {
      const o = row as Record<string, unknown>;
      return {
        id: toNumber(o.id),
        external_id: o?.external_id == null ? null : toNumber(o.external_id),
        first_name: toText(o.first_name),
        last_name: toText(o.last_name),
        month: toText(o.month),
        dive_groups_count: toNumber(o.dive_groups_count),
        dives_count: toNumber(o.dives_count),
      };
    });
  }
}
